// validationRules.js

const namePattern = /^[A-Za-z\s]+$/;
const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const phonePattern = /^[6-9]\d{9}$/;
const addressPattern = /^[A-Za-z0-9\s,./#-]+$/;
const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const bankAccountPattern = /^\d{9,18}$/;
const employeeCodePattern = /^[A-Za-z0-9_-]+$/;
const twelveDigitPattern = /^\d{12}$/;
const alphaNumericPattern = /^[A-Za-z0-9]+$/;

const optionalPatternRule = (pattern, message, formatValue = (value) => value) => ({
  validate: (value) => {
    if (!value) return true;
    return pattern.test(formatValue(value)) || message;
  },
});

export const employeeValidationRules = {
  first_name: [
    { required: true, message: "First name is required" },
    { pattern: namePattern, message: "Only letters and spaces allowed" },
  ],
  last_name: [
    { required: true, message: "Last name is required" },
    { pattern: namePattern, message: "Only letters and spaces allowed" },
  ],
  email: [
    { required: true, message: "Email is required" },
    { pattern: emailPattern, message: "Enter a valid email address" },
  ],
  phone: [
    { required: true, message: "This field is required" },
    { pattern: phonePattern, message: "Type a valid phone number" },
  ],
  emergency_contact_no: [
    { required: true, message: "This field is required" },
    { pattern: phonePattern, message: "Type a valid phone number" },
  ],
  current_address: [
    optionalPatternRule(
      addressPattern,
      "Address contains invalid characters",
    ),
  ],
  permanent_address: [
    optionalPatternRule(
      addressPattern,
      "Address contains invalid characters",
    ),
  ],
  AadhaarNo: [
    optionalPatternRule(twelveDigitPattern, "Aadhaar must be exactly 12 digits"),
  ],
  pan_number: [
    optionalPatternRule(
      panPattern,
      "Invalid PAN number (e.g., ABCDE1234F)",
      (value) => value.toUpperCase(),
    ),
  ],
  ifsc_code: [
    optionalPatternRule(
      ifscPattern,
      "Invalid IFSC code (e.g., SBIN0001234)",
      (value) => value.toUpperCase(),
    ),
  ],
  bank_account_number: [
    optionalPatternRule(
      bankAccountPattern,
      "Account number must be 9-18 digits",
    ),
  ],
  employee_code: [
    { required: true, message: "Employee code is required" },
    {
      pattern: employeeCodePattern,
      message: "Only letters, numbers, _ and - allowed",
    },
    {
      validate: (value) => {
        if (!value) return true;
        return /[A-Za-z]/.test(value) || "Employee code must contain at least one letter";
      },
    },
  ],
  date_of_birth: [
    { required: true, message: "Date of birth is required" },
    {
      validate: (value) => {
        if (!value) return true;

        const dob = new Date(value);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        const hasBirthdayPassed =
          monthDiff > 0 ||
          (monthDiff === 0 && today.getDate() >= dob.getDate());

        return (
          age > 18 ||
          (age === 18 && hasBirthdayPassed) ||
          "Employee must be at least 18 years old"
        );
      },
    },
  ],
  joining_date: [
    {
      validate: (value) => {
        if (!value) return true;
        return (
          new Date(value) <= new Date() || "Joining date cannot be in the future"
        );
      },
    },
  ],
  uan_number: [
    optionalPatternRule(twelveDigitPattern, "UAN must be exactly 12 digits"),
  ],
  pf_number: [
    optionalPatternRule(
      alphaNumericPattern,
      "Only alphanumeric characters allowed",
    ),
  ],
  esi_number: [
    optionalPatternRule(
      alphaNumericPattern,
      "Only alphanumeric characters allowed",
    ),
  ],
  role_id: [{ required: true, message: "Role is required" }],
};

// Department Validation Rules
export const departmentValidationRules = {
  name: [
    { required: true, message: "Department name is required" },
    {
      validate: (value) => {
        if (!value) return true;
        // Allow letters, numbers, spaces, and common special characters
        const pattern = /^[A-Za-z0-9\s&.,'()-]+$/;
        return pattern.test(value) || "Only letters, numbers, spaces, and &.,'()- allowed";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.trim().length >= 2 || "Department name must be at least 2 characters";
      },
    },
  ],
  code: [
    { required: true, message: "Department code is required" },
    {
      pattern: /^[A-Za-z0-9_-]+$/,
      message: "Only letters, numbers, _ and - allowed",
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.length >= 3 || "Code must be at least 3 characters";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.length <= 20 || "Code must not exceed 20 characters";
      },
    },
  ],
  parent_department_id: [
    {
      validate: (value) => {
        if (!value) return true;
        const num = Number(value);
        return (Number.isInteger(num) && num > 0) || "Must be a valid positive number";
      },
    },
  ],
  budget: [
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return (!isNaN(num) && num >= 0) || "Budget must be a positive number";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return num <= 999999999 || "Budget is too large";
      },
    },
  ],
  description: [
    {
      validate: (value) => {
        if (!value) return true;
        // Allow common punctuation and special characters in description
        const pattern = /^[A-Za-z0-9\s.,!?&()'"/\-]+$/;
        return pattern.test(value) || "Description contains invalid characters";
      },
    },
    {
      maxLength: 500,
      message: "Description cannot exceed 500 characters",
    },
    {
      minLength: 0,
      message: "",
    },
  ],
};


export const designationValidationRules = {
  department_id: [
    { required: true, message: "Department is required" },
    {
      validate: (value) => {
        if (!value) return true;
        const num = Number(value);
        return (Number.isInteger(num) && num > 0) || "Must be a valid department";
      },
    },
  ],
  name: [
    { required: true, message: "Designation name is required" },
    {
      validate: (value) => {
        if (!value) return true;
        const pattern = /^[A-Za-z0-9\s&.,'()/-]+$/;
        return pattern.test(value) || "Only letters, numbers, spaces, and &.,'()/- allowed";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.trim().length >= 2 || "Name must be at least 2 characters";
      },
    },
  ],
  code: [
    { required: true, message: "Designation code is required" },
    {
      pattern: /^[A-Za-z0-9_-]+$/,
      message: "Only letters, numbers, _ and - allowed",
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.length >= 2 || "Code must be at least 2 characters";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        return value.length <= 20 || "Code must not exceed 20 characters";
      },
    },
  ],
  level: [
    { required: true, message: "Level is required" },
    {
      validate: (value) => {
        if (!value) return true;
        const num = Number(value);
        return (Number.isInteger(num) && num >= 1 && num <= 5) || "Level must be between 1 and 5";
      },
    },
  ],
  description: [
    {
      validate: (value) => {
        if (!value) return true;
        const pattern = /^[A-Za-z0-9\s.,!?&()'"/\-]+$/;
        return pattern.test(value) || "Description contains invalid characters";
      },
    },
    {
      maxLength: 500,
      message: "Description cannot exceed 500 characters",
    },
  ],
  salary_range_min: [
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return (!isNaN(num) && num >= 0) || "Minimum salary must be a positive number";
      },
    },
  ],
  salary_range_max: [
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return (!isNaN(num) && num >= 0) || "Maximum salary must be a positive number";
      },
    },
    {
      validate: (value, formData) => {
        if (!value || !formData.salary_range_min) return true;
        const min = parseFloat(formData.salary_range_min);
        const max = parseFloat(value);
        return max > min || "Maximum salary must be greater than minimum salary";
      },
    },
  ],
};
const countSpecialChars = (value) =>
  (value.match(/[^A-Za-z0-9\s]/g) || []).length;

export const projectValidationRules = {
  name: [
    { required: true, message: "Project name is required" },
    {
      validate: (value) => {
        if (!value) return true;
        return (
          countSpecialChars(value) <= 3 ||
          "No more than 3 special characters allowed"
        );
      },
    },
  ],
  code: [
    { required: true, message: "Project code is required" },
    {
      pattern: /^[A-Za-z0-9_-]+$/,
      message: "Only letters, numbers, _ and - allowed",
    },
    {
      validate: (value) => {
        if (!value) return true;
        return (
          countSpecialChars(value) <= 3 ||
          "No more than 3 special characters allowed"
        );
      },
    },
  ],
  budget: [
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return (!isNaN(num) && num >= 0) || "Budget must be a positive number";
      },
    },
    {
      validate: (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return num <= 999999999999.99 || "Budget is too large";
      },
    },
  ],
};